import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import './InstallPrompt.css';
import { useTranslation } from 'react-i18next';

/** Evenimentul Chrome-only care oferă instalarea; nu e în lib.dom. */
interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const HIDDEN = 'df.installHidden';

/**
 * „Instalează aplicația" — banda de jos, o dată.
 *
 * Recepția ține ecranul deschis toată ziua pe o tabletă, iar bara browserului
 * e spațiu luat de la calendar. Browserul decide singur când oferă instalarea
 * (manifest, service worker, câteva vizite), iar noi doar prindem momentul:
 * dacă evenimentul nu vine — pe Safari nu vine niciodată — nu se desenează
 * nimic, ceea ce e răspunsul corect, nu o instrucțiune pe care nimeni n-o
 * poate urma.
 *
 * Închis o dată, rămâne închis: alegerea se ține în `localStorage`, nu în
 * sesiune, fiindcă „nu, mulțumesc" nu se schimbă la fiecare reîncărcare.
 */
export function InstallPrompt() {
  const { t } = useTranslation('common');
  const [offer, setOffer] = useState<InstallEvent | null>(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(HIDDEN) === '1') return;
    } catch {
      /* private mode: se oferă de fiecare dată, ceea ce e acceptabil */
    }
    const onOffer = (e: Event) => {
      e.preventDefault();
      setOffer(e as InstallEvent);
    };
    window.addEventListener('beforeinstallprompt', onOffer);
    // Instalată din meniul browserului: banda n-are ce mai oferi.
    const onInstalled = () => setOffer(null);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onOffer);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const hide = () => {
    setOffer(null);
    try {
      localStorage.setItem(HIDDEN, '1');
    } catch {
      /* private mode */
    }
  };

  if (!offer) return null;

  return (
    <div className="inst" role="region" aria-label={t('instaleazaAplicatia')}>
      <Download size={15} aria-hidden />
      <span>{t('instaleazaAplicatiaPeAcest')}</span>
      <button
        type="button"
        className="inst__go"
        onClick={() => {
          void offer.prompt().then(() => hide());
        }}
      >
        {t('instaleaza')}
      </button>
      <button type="button" className="inst__close" aria-label={t('inchide')} onClick={hide}>
        <X size={14} />
      </button>
    </div>
  );
}
